import React, { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, X, Package, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { dataService } from '@/services/data-service';

interface Asset {
  id: string;
  code: string;
  name: string;
  location?: string;
  category?: string;
}

interface AssetSearchWithSuggestionsProps {
  onAssetSelect: (asset: Asset) => void;
  placeholder?: string;
  className?: string;
}

export function AssetSearchWithSuggestions({
  onAssetSelect,
  placeholder = 'Search assets...',
  className,
}: Readonly<AssetSearchWithSuggestionsProps>) {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState<Asset[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const searchAssets = async () => {
      if (searchTerm.trim().length > 0) {
        setIsLoading(true);
        try {
          // Fetch assets from the data service
          const response = await dataService.getAssets({ 
            pageSize: 50,
            searchTerm: searchTerm 
          });
          
          const filtered: Asset[] = response.data.map((asset: any) => ({
            id: asset.assetId || asset.id,
            code: asset.code || asset.assetId,
            name: asset.name,
            location: asset.location,
            category: asset.category,
          }));
          
          setSuggestions(filtered);
          setShowSuggestions(true);
          setSelectedIndex(-1);
        } catch (error) {
          console.error('Failed to search assets:', error);
          setSuggestions([]);
        } finally {
          setIsLoading(false);
        }
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    };

    // Debounce the search
    const timeoutId = setTimeout(searchAssets, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelectAsset(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
    }
  };

  const handleSelectAsset = (asset: Asset) => {
    onAssetSelect(asset);
    setSearchTerm('');
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  const handleClear = () => {
    setSearchTerm('');
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  return (
    <div ref={wrapperRef} className={cn('relative', className)}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          type="text"
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => searchTerm && setShowSuggestions(true)}
          className="pl-9 pr-9 bg-background"
        />
        {isLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        )}
        {!isLoading && searchTerm && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {showSuggestions && !isLoading && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-lg shadow-lg max-h-[300px] overflow-y-auto">
          {suggestions.map((asset, index) => (
            <button
              key={asset.id}
              onClick={() => handleSelectAsset(asset)}
              className={cn(
                'w-full text-left px-4 py-3 hover:bg-accent transition-colors border-b last:border-b-0',
                selectedIndex === index && 'bg-accent'
              )}
            >
              <div className="flex items-start gap-3">
                <div className="mt-1">
                  <Package className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm">{asset.code}</span>
                    {asset.category && (
                      <Badge variant="secondary" className="text-xs">
                        {asset.category}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-foreground truncate">{asset.name}</p>
                  {asset.location && (
                    <p className="text-xs text-muted-foreground mt-1">{asset.location}</p>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {showSuggestions && !isLoading && searchTerm && suggestions.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-lg shadow-lg p-4 text-center">
          <p className="text-sm text-muted-foreground">No assets found</p>
        </div>
      )}
    </div>
  );
}
